import React, { useMemo, type ReactNode } from 'react';

import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import {
	useRouteName,
	createRouteParamHook,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { VIEW_PAGE, VIEW_BLOG } from '@confluence/named-routes/entry-points/routes.ts';
import { useAccessStatus } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { PRELOADED_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/view-page-onboarding-keys.ts';
import {
	ONBOARDING_EXPERIENCES,
	OnboardingKeysCombined,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { OnboardingPersonalizationContext } from '@confluence/onboarding-helpers/entry-points/OnboardingPersonalizationContext.ts';

const useRouteContentId = createRouteParamHook('contentId');

export const OnboardingPersonalization = ({
	contentId,
	spaceKey,
	children,
}: {
	contentId: string;
	spaceKey?: string;
	children: ReactNode;
}): React.JSX.Element => {
	const routeName = useRouteName({ selector: (name) => name });
	const routeContentId = useRouteContentId();
	const { isLicensed, isGuest } = useAccessStatus();
	const eligible =
		(routeName === VIEW_PAGE.name || routeName === VIEW_BLOG.name) &&
		routeContentId === contentId &&
		!!spaceKey &&
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_personalization_onboarding_reader');
	let onboardingKeys: readonly string[] | undefined;
	let includeFoundingAdmin = false;
	if (eligible) {
		const includeOnboarding =
			(isLicensed || isGuest) && expVal('confluence_pgxp_p2m_batch_1', 'isEnabled', false);
		let includeOmnibar = false;
		if (fg('cc-ai-growth-preload-omnibar')) includeOmnibar = true;
		if (
			(includeOnboarding || includeOmnibar) &&
			expVal('confluence_exp_personalization_contract', 'isEnabled', false)
		) {
			if (includeOnboarding) {
				onboardingKeys = fg('confluence_preload_all_onboarding_keys')
					? OnboardingKeysCombined
					: PRELOADED_KEYS;
			}
			includeFoundingAdmin = includeOmnibar;
		}
	}
	const value = useMemo(
		() =>
			onboardingKeys || includeFoundingAdmin
				? {
						keys: [
							...new Set([
								...(onboardingKeys ?? []),
								...(includeFoundingAdmin ? [ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN] : []),
							]),
						],
					}
				: undefined,
		[onboardingKeys, includeFoundingAdmin],
	);
	// Keep the page/editor mounted across eligibility and cache changes. The existing non-suspending
	// onboarding hook observes the parent's exact key union in Relay's normalized store.
	return (
		<OnboardingPersonalizationContext.Provider value={value}>
			{children}
		</OnboardingPersonalizationContext.Provider>
	);
};
